import { Button, Grid, Stack } from "@mui/material";

import { BranchAdminShipperMultiSelect } from "components/BranchAdminShipperMultiSelect";
import { BranchMultiSelect } from "components/BranchMultiSelect";
import { CompanyAdminShipperMultiSelect } from "components/CompanyAdminShipperMultiSelect";
import { ResetFiltersButton } from "components/core/ResetFiltersButton.tsx";
import { EquipmentTypeMultiSelect } from "components/EquipmentTypeSelect";
import { ProvinceStateMultiSelect } from "components/ProvinceStateMultiSelect";
import { ShipmentServiceMultiSelect } from "components/ShipmentServiceMultiSelect";
import { ShipmentStatusMultiSelect } from "components/ShipmentStatusSelect";
import { useRoleContext } from "hooks";

import { AdminPrivilegeFiltersContainer } from "./components/AdminPrivilegeFiltersContainer";
import { FilterShipmentByTypeDropdown } from "./components/FilterShipmentByTypeDropdown";
import { IncludeBranchShipmentsCheckbox } from "./components/IncludeBranchShipmentsCheckbox";
import { IncludeCompanyShipmentsCheckbox } from "./components/IncludeCompanyShipmentsCheckbox";
import { ShipmentSearchByName } from "./components/ShipmentSearchByName";

interface Props {
  filters: ShipmentsFilterState;
  filterToolsAreVisible: boolean;
  toggleFilterTools: (value: boolean) => void;
  onLocationChanged: (
    key: keyof Location,
    addressKey: keyof ShipmentLocationFilter,
    value: string[],
  ) => void;
  onEquipmentTypeChanged: (value: string[]) => void;
  onServiceTypeChanged: (value: string[]) => void;
  onShipmentStatusChanged: (value: string[]) => void;
  updateIncludeCompanyShipments: (value: boolean) => void;
  updateIncludeBranchShipments: (value: boolean) => void;
  updateBranches: (value: string[]) => void;
  updateShippers: (value: string[]) => void;
  onClearFilters: VoidFunction;
}

export function ShipmentsFilter(props: Props) {
  const {
    filters,
    filterToolsAreVisible,
    toggleFilterTools,
    onLocationChanged,
    onEquipmentTypeChanged,
    onServiceTypeChanged,
    onShipmentStatusChanged,
    updateIncludeCompanyShipments,
    updateIncludeBranchShipments,
    updateBranches,
    updateShippers,
    onClearFilters,
  } = props;
  const { hasBranchAdminRole, hasCompanyAdminRole, listBranchAdminRoles } =
    useRoleContext();

  return (
    <Stack direction="column" spacing={2}>
      <Grid container spacing={1} pt={1}>
        <Grid item xs={12} md={6}>
          <ShipmentSearchByName tabType="regular" sx={{ width: "100%" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ShipmentStatusMultiSelect
            defaultValue={filters.shipmentStatus}
            value={filters.shipmentStatus}
            onChange={(newValue) => onShipmentStatusChanged(newValue)}
            excludeStatuses={[]}
            width="100%"
          />
        </Grid>
        {filterToolsAreVisible && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <EquipmentTypeMultiSelect
                onChange={onEquipmentTypeChanged}
                defaultValue={filters.equipmentTypes}
                value={filters.equipmentTypes}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ShipmentServiceMultiSelect
                label="Services"
                value={filters.services}
                onChange={(newValue) => onServiceTypeChanged(newValue)}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ProvinceStateMultiSelect
                label="Origins"
                value={filters.origin.provinces}
                onChange={(value) =>
                  onLocationChanged("origin", "provinces", value)
                }
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ProvinceStateMultiSelect
                label="Destinations"
                value={filters.destination.provinces}
                onChange={(value) =>
                  onLocationChanged("destination", "provinces", value)
                }
                width="100%"
              />
            </Grid>
            <Grid item xs={12}>
              <FilterShipmentByTypeDropdown width="100%" />
            </Grid>
            <Grid item xs={12}>
              <AdminPrivilegeFiltersContainer>
                {hasCompanyAdminRole() && (
                  <IncludeCompanyShipmentsCheckbox
                    value={filters.includeCompanyShipments}
                    onChange={updateIncludeCompanyShipments}
                  />
                )}
                {hasBranchAdminRole() &&
                  listBranchAdminRoles().length > 1 &&
                  !hasCompanyAdminRole() && (
                    <IncludeBranchShipmentsCheckbox
                      value={filters.includeBranchShipments}
                      onChange={updateIncludeBranchShipments}
                    />
                  )}
                {(hasBranchAdminRole() || hasCompanyAdminRole()) && (
                  <BranchMultiSelect
                    value={filters.branches}
                    onChange={updateBranches}
                  />
                )}
                {hasCompanyAdminRole() && (
                  <CompanyAdminShipperMultiSelect
                    value={filters.shippers}
                    onChange={updateShippers}
                    selectedBranchIds={filters.branches}
                  />
                )}
                {hasBranchAdminRole() && !hasCompanyAdminRole() && (
                  <BranchAdminShipperMultiSelect
                    value={filters.shippers}
                    onChange={updateShippers}
                    selectedBranchIds={filters.branches}
                  />
                )}
              </AdminPrivilegeFiltersContainer>
            </Grid>
          </>
        )}
      </Grid>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        paddingLeft={1}
      >
        <Button
          variant="outlined"
          onClick={() => toggleFilterTools(!filterToolsAreVisible)}
        >
          {filterToolsAreVisible ? "Less" : "More"}
        </Button>
        <ResetFiltersButton handleResetFilters={onClearFilters} />
      </Stack>
    </Stack>
  );
}
